import React from 'react';

/**
 * 基础骨架屏组件
 * @param {Object} props
 * @param {string} [props.className=''] - CSS类名
 * @param {boolean} [props.circle=false] - 是否为圆形
 */
export const Skeleton = ({ className = '', circle = false }) => (
  <div 
    className={`animate-pulse bg-gradient-to-r from-pink-100 to-love-pink/20 ${
      circle ? 'rounded-full' : 'rounded-xl'
    } ${className}`}
    aria-hidden="true"
  />
);

/**
 * 卡片骨架屏组件
 */
export const CardSkeleton = () => (
  <div className="love-card p-6 space-y-4" aria-hidden="true">
    <Skeleton className="h-48 w-full rounded-xl" />
    <Skeleton className="h-6 w-3/4" />
    <Skeleton className="h-4 w-1/2" />
  </div>
);

/**
 * 列表骨架屏组件
 * @param {Object} props
 * @param {number} [props.count=4] - 骨架项数量
 */
export const ListSkeleton = ({ count = 4 }) => (
  <div className="space-y-4" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="love-card p-4 flex gap-4">
        <Skeleton className="w-16 h-16 rounded-xl flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * 页面骨架屏组件
 */
export const PageSkeleton = () => (
  <div className="min-h-screen flex" aria-hidden="true">
    {/* 侧边栏骨架 */}
    <div className="w-64 bg-white border-r border-pink-200 p-6 space-y-4">
      <Skeleton className="h-8 w-32" />
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-xl" />
      ))}
    </div>
    
    {/* 主内容骨架 */}
    <div className="flex-1 p-8 space-y-6">
      <Skeleton className="h-8 w-48" />
      <div className="grid md:grid-cols-2 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    </div>
  </div>
);

/**
 * 文本骨架屏组件
 * @param {Object} props
 * @param {number} [props.lines=3] - 文本行数
 */
export const TextSkeleton = ({ lines = 3 }) => (
  <div className="space-y-2" aria-hidden="true">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton 
        key={i} 
        className={`h-4 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} 
      />
    ))}
  </div>
);

/**
 * 图片骨架屏组件
 * @param {Object} props
 * @param {string} [props.className=''] - CSS类名
 */
export const ImageSkeleton = ({ className = '' }) => (
  <Skeleton className={`${className}`} />
);

/**
 * 模型卡片骨架屏组件
 */
export const ModelCardSkeleton = () => (
  <div className="love-card overflow-hidden" aria-hidden="true">
    <Skeleton className="h-40 md:h-64 w-full" />
    <div className="p-4 md:p-6 space-y-2">
      <Skeleton className="h-6 w-24" />
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-4 w-full" />
    </div>
  </div>
);

/**
 * Hero区域骨架屏组件
 */
export const HeroSkeleton = () => (
  <div className="py-20 px-4 text-center" aria-hidden="true">
    <Skeleton className="h-12 md:h-16 w-3/4 max-w-2xl mx-auto mb-6" />
    <Skeleton className="h-6 w-1/2 max-w-xl mx-auto mb-8" />
    <Skeleton className="h-12 w-40 mx-auto rounded-full" />
  </div>
);

export default {
  Skeleton,
  CardSkeleton,
  ListSkeleton,
  PageSkeleton,
  TextSkeleton,
  ImageSkeleton,
  ModelCardSkeleton,
  HeroSkeleton
};
